import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';

// Contextes
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

// Layout
import BottomNav from './components/Layout/BottomNav';
import InstallBanner from './components/UI/InstallBanner';

// Pages publiques
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Pages joueur
import Home from './pages/Home';
import Quiz from './pages/Quiz';
import ChronoMode from './pages/ChronoMode';
import Duel from './pages/Duel';
import MultiPlayer from './pages/MultiPlayer';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Shop from './pages/Shop';
import Tournament from './pages/Tournament';
import Championships from './pages/Championships';
import ChampionshipDetail from './pages/ChampionshipDetail';
import ChampionshipQuiz from './pages/ChampionshipQuiz';
import Themes from './pages/Themes';
import ThemeQuiz from './pages/ThemeQuiz';
import PaymentSuccess from './pages/PaymentSuccess';

// Pages admin
import AdminLayout from './pages/Admin/AdminLayout';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminQuestions from './pages/Admin/AdminQuestions';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminTournaments from './pages/Admin/AdminTournaments';
import AdminSettings         from './pages/Admin/AdminSettings';
import AdminChampionships    from './pages/Admin/AdminChampionships';
import AdminReferral         from './pages/Admin/AdminReferral';
import AdminThemes           from './pages/Admin/AdminThemes';
import AdminCoins            from './pages/Admin/AdminCoins';
import AdminShop             from './pages/Admin/AdminShop';

// ─── Route protégée ───────────────────────────────────────────────────────────
function PrivateRoute({ children, adminOnly = false }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: '#0d2b1a' }}
      >
        <div
          className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin"
          style={{ borderColor: '#f0b429', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Un non-admin qui tente d'accéder à l'admin → redirigé vers /
  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ─── Layout avec BottomNav ────────────────────────────────────────────────────
function PlayerLayout({ children }) {
  return (
    <div className="relative">
      {children}
      <BottomNav />
    </div>
  );
}

// ─── Routeur principal ────────────────────────────────────────────────────────
function AppRouter() {
  return (
    <BrowserRouter>
      <SocketProvider>
        <RoutesWithTransitions />
      </SocketProvider>
    </BrowserRouter>
  );
}

const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password'];

function RoutesWithTransitions() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
          {/* Routes publiques */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Routes joueur protégées */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <PlayerLayout>
                  <Home />
                </PlayerLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/quiz"
            element={
              <PrivateRoute>
                <Quiz />
              </PrivateRoute>
            }
          />
          <Route
            path="/chrono"
            element={
              <PrivateRoute>
                <ChronoMode />
              </PrivateRoute>
            }
          />
          <Route
            path="/duel"
            element={
              <PrivateRoute>
                <Duel />
              </PrivateRoute>
            }
          />
          <Route
            path="/multi"
            element={
              <PrivateRoute>
                <MultiPlayer />
              </PrivateRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <PrivateRoute>
                <PlayerLayout>
                  <Leaderboard />
                </PlayerLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile/:id"
            element={
              <PrivateRoute>
                <PlayerLayout>
                  <Profile />
                </PlayerLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/shop"
            element={
              <PrivateRoute>
                <PlayerLayout>
                  <Shop />
                </PlayerLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tournament"
            element={
              <PrivateRoute>
                <PlayerLayout>
                  <Tournament />
                </PlayerLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/championships"
            element={
              <PrivateRoute>
                <Championships />
              </PrivateRoute>
            }
          />
          <Route
            path="/championships/:id/quiz"
            element={
              <PrivateRoute>
                <ChampionshipQuiz />
              </PrivateRoute>
            }
          />
          <Route
            path="/championships/:id"
            element={
              <PrivateRoute>
                <ChampionshipDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/themes"
            element={
              <PrivateRoute>
                <PlayerLayout>
                  <Themes />
                </PlayerLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/themes/:themeId"
            element={
              <PrivateRoute>
                <ThemeQuiz />
              </PrivateRoute>
            }
          />

          <Route
            path="/payment/success"
            element={
              <PrivateRoute>
                <PaymentSuccess />
              </PrivateRoute>
            }
          />

          {/* Routes admin protégées */}
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly>
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="tournaments" element={<AdminTournaments />} />
            <Route path="championships" element={<AdminChampionships />} />
            <Route path="referral" element={<AdminReferral />} />
            <Route path="coins"    element={<AdminCoins />} />
            <Route path="shop"     element={<AdminShop />} />
            <Route path="themes"   element={<AdminThemes />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
  );
}

// ─── App root avec providers ──────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      {/* Notifications toast */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1a4a2e',
            color: '#ffffff',
            border: '1px solid rgba(240, 180, 41, 0.3)',
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: '600',
            borderRadius: '12px',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#1a4a2e',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#1a4a2e',
            },
          },
        }}
      />
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
